"use client";
import { EmbedContext } from "./context";
import { ActivePlayerType, createEmbedEventBus } from "./event-bus";
import { EmbedDataType } from "./embed.types";
import {
  useCallback,
  useEffect,
  useInsertionEffect,
  useMemo,
  useState,
} from "react";
import { PostDetailsType } from "@genuin/components/react-query/api/feed/schema";
import { createEmbedRouter } from "./embed-router";

type EmbedProviderProps = {
  embedData: EmbedDataType;
  children: React.ReactNode;
  container: HTMLElement;
};

/**
 * EmbedProvider component that provides the embed context to its children.
 * @param param0 EmbedProviderProps - The properties for the EmbedProvider component.
 * @property embedData - The data to be provided to the embed context.
 * @returns
 */
export function EmbedProvider({
  embedData,
  children,
  container,
}: EmbedProviderProps) {
  const [stateEmbedData, setStateEmbedData] = useState(embedData);
  // Create a unique event bus for this provider instance
  const embedEventBus = useMemo(
    () =>
      createEmbedEventBus({
        activePlayerType: "embed",
        activeIndex: 0,
        sectionList: [],
        isSectioned: false,
        containerInView: true,
      }),
    []
  );

  // Create a unique router for this provider instance
  const embedRouter = useMemo(() => createEmbedRouter(), []);

  // Mount event listeners and handlers
  useInsertionEffect(() => {
    if (!window.genuin) return;

    const handleUpdateContextualParams = (props: any) => {
      const payload = props.payload;
      if (
        payload &&
        (payload.embedId === stateEmbedData.embed_id ||
          payload.placementId === stateEmbedData.placement_id) &&
        payload.contextualParams
      ) {
        setStateEmbedData((prev) => ({
          ...prev,
          contextualParams: payload.contextualParams,
        }));
      }
    };

    const handleUpdateStartVideoSlug = (props: any) => {
      const payload = props.payload;
      if (
        payload &&
        (payload.embedId === stateEmbedData.embed_id ||
          payload.placementId === stateEmbedData.placement_id) &&
        payload.startVideoSlug
      ) {
        setStateEmbedData((prev) => ({
          ...prev,
          startVideoSlug: payload.startVideoSlug,
          autoUserInteractionToPerform: payload.action,
        }));
      }
    };

    window.genuin.on(
      "sdk:updateContextualParams",
      handleUpdateContextualParams
    );
    window.genuin.on("sdk:updateStartVideoSlug", handleUpdateStartVideoSlug);

    return () => {
      window.genuin?.off(
        "sdk:updateContextualParams",
        handleUpdateContextualParams
      );
      window.genuin?.off(
        "sdk:updateStartVideoSlug",
        handleUpdateStartVideoSlug
      );
    };
  }, []);

  // Notify that the embed provider is ready
  useEffect(() => {
    if (!window.genuin) return;
    // Signal that the embed provider is ready to receive events
    window.genuin?.emit("sdk:embedProviderReady", {
      embedId: stateEmbedData.embed_id,
      placementId: stateEmbedData.placement_id,
    });
  }, []);

  const updateSectionList = useCallback(
    // Updates the section list in the embed context and emits a sectionListChange event
    (newSectionList: PostDetailsType["section"][]) => {
      embedEventBus.emit("sectionListChange", undefined, (currentContext) => ({
        ...currentContext,
        sectionList: newSectionList,
      }));
    },
    [embedEventBus]
  );

  const updateIsSectioned = useCallback(
    // Updates the isSectioned flag in the embed context and emits an isSectionedChange event
    (sectioned: boolean) => {
      embedEventBus.emit("isSectionedChange", undefined, (currentContext) => ({
        ...currentContext,
        isSectioned: sectioned,
      }));
    },
    [embedEventBus]
  );

  const updateSelectedSection = useCallback(
    // Updates the selected section in the embed context and emits a selectedSectionChange event
    (section: PostDetailsType["section"] | null) => {
      embedEventBus.emit(
        "selectedSectionChange",
        undefined,
        (currentContext) => ({
          ...currentContext,
          selectedSection: section,
        })
      );
    },
    [embedEventBus]
  );

  const changeActiveIndex = useCallback(
    (newIndex: number) => {
      embedEventBus.emit("activeIndexChange", undefined, (currentContext) => ({
        ...currentContext,
        activeIndex: newIndex,
        previousIndex: currentContext.activeIndex,
        shouldTrackImpression: newIndex !== currentContext.activeIndex,
      }));
    },
    [embedEventBus]
  );

  const changeActivePlayerType = useCallback(
    (newActiveType: ActivePlayerType, activeIndex?: number) => {
      if (newActiveType === embedEventBus.getContext().activePlayerType) return;
      embedEventBus.emit(
        "activePlayerTypeChange",
        undefined,
        (currentContext) => ({
          ...currentContext,
          previousPlayerType: currentContext.activePlayerType,
          activePlayerType: newActiveType,
          activeIndex: activeIndex ?? currentContext.activeIndex,
          previousIndex: currentContext.activeIndex,
          shouldTrackImpression: activeIndex !== currentContext.activeIndex,
        })
      );
    },
    [embedEventBus]
  );

  useEffect(() => {
    if (embedData.startVideoSlug) changeActivePlayerType("expand-view");
  }, [changeActivePlayerType, embedData.startVideoSlug]);

  const goBackToPreviousPlayerType = useCallback(() => {
    embedEventBus.emit(
      "activePlayerTypeChange",
      undefined,
      (currentContext) => ({
        ...currentContext,
        activePlayerType: currentContext.previousPlayerType ?? "embed",
      })
    );
  }, [embedEventBus]);

  const markAutoInteractionActionDone = useCallback(() => {
    const oldContext = embedEventBus.getContext();
    embedEventBus.updateContext({
      ...oldContext,
      autoInteractionActionDone: true,
    });
  }, [embedEventBus]);

  // Observe the container for visibility changes to handle floating view behavior and track in-view status
  useEffect(() => {
    const element = container;
    if (!element) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const inView = entry.isIntersecting;
        const currentContext = embedEventBus.getContext();

        // Emit event if container in-view status changed
        if (inView !== currentContext.containerInView) {
          embedEventBus.emit(
            "containerInViewChange",
            undefined,
            (currentContext) => ({
              ...currentContext,
              containerInView: inView,
            })
          );
        }

        // Handle floating view behavior only if enabled
        if (!embedData.customization.is_floating_view) return;

        if (!entry.isIntersecting) {
          changeActivePlayerType("pip");
        } else {
          const embedContext = embedEventBus.getContext();
          // In case of new player-type is expand view don't close the pip view yet. because intersection observer will be triggered again if expand-view opens.
          if (embedContext.activePlayerType === "expand-view") return;
          changeActivePlayerType("embed");
        }
      });
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [container, changeActivePlayerType, embedData, embedEventBus]);
  return (
    <EmbedContext.Provider
      value={{
        rootElement: container,
        embedData: stateEmbedData,
        customization: stateEmbedData.customization,
        embedEventBus,
        embedRouter,
        changeActiveIndex,
        changeActivePlayerType,
        goBackToPreviousPlayerType,
        updateSectionList,
        updateIsSectioned,
        updateSelectedSection,
        markAutoInteractionActionDone,
      }}
    >
      {children}
    </EmbedContext.Provider>
  );
}
